import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import {
  FaRupeeSign,
  FaSyncAlt,
  FaFilter,
  FaUsers,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaRoute,
  FaBus,
  FaMapMarkerAlt,
  FaUserGraduate,
  FaReceipt,
  FaPlus,
  FaCheck,
  FaMinus,
  FaLock,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/transport-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade4} 0%, ${COLORS.primary_color_shade6} 55%, ${COLORS.primary_color_shade8} 100%)`;

function formatINR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMoneyValue(value) {
  if (value === "" || value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return formatINR(n);
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function formatServiceMonth(applicableMonth) {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (!applicableMonth || !String(applicableMonth).includes("-")) {
    return applicableMonth || "—";
  }

  const [year, month] = String(applicableMonth).split("-");
  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return applicableMonth;
  return `${monthNames[monthIndex]} ${year}`;
}

function formatSessionStatus(status) {
  if (!status) return "—";
  const normalized = String(status).trim();
  if (!normalized) return "—";
  if (normalized.toLowerCase() === "current") return "Current";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getRowPaymentClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "paid" || value === "due settled") return "mtf-row--paid";
  if (value === "due" || value === "sr in progress" || value === "not paid") {
    return "mtf-row--due";
  }
  return "";
}

function getPaidAmountClass(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num) || num <= 0) return "mtf-amount mtf-amount--neutral";
  return "mtf-amount mtf-amount--paid";
}

function getDueAmountClass(amount, status) {
  const value = String(status || "").toLowerCase();
  if (value === "paid" || value === "due settled") {
    return "mtf-amount mtf-amount--clear";
  }

  const num = Number(amount);
  if (!Number.isFinite(num) || num <= 0) return "mtf-amount mtf-amount--neutral";
  return "mtf-amount mtf-amount--due";
}

function isStudentEligibleForAddFee(row) {
  const status = String(row.payment_status || "").toLowerCase();
  if (status === "paid" || status === "due settled") return false;

  const paid = Number(row.amount_paid);
  const settled = Number(row.amount_settled);
  if (Number.isFinite(paid) && paid > 0) return false;
  if (Number.isFinite(settled) && settled > 0) return false;
  return true;
}

function TableHeaderCell({ label, icon: Icon, className = "" }) {
  return (
    <th className={`mtf-th ${className}`.trim()}>
      <span className="mtf-th__inner">
        {Icon ? (
          <span className="mtf-th__icon" aria-hidden>
            <Icon />
          </span>
        ) : null}
        <span className="mtf-th__label">{label}</span>
      </span>
    </th>
  );
}

function AddFeeCheckbox({
  checked = false,
  indeterminate = false,
  disabled = false,
  locked = false,
  nested = false,
  onChange,
  title,
  className = "",
  inputClassName = "",
  "aria-label": ariaLabel,
}) {
  if (locked) {
    return (
      <span
        className={`mtf-checkbox mtf-checkbox--locked ${className}`.trim()}
        title={title || "Paid or settled — cannot select"}
      >
        <span className="mtf-checkbox__box" aria-hidden>
          <FaLock />
        </span>
      </span>
    );
  }

  const stateClass = checked
    ? " mtf-checkbox--checked"
    : indeterminate
      ? " mtf-checkbox--indeterminate"
      : "";
  const Wrapper = nested ? "span" : "label";

  return (
    <Wrapper
      className={`mtf-checkbox${stateClass}${
        disabled ? " mtf-checkbox--disabled" : ""
      } ${className}`.trim()}
      title={title}
    >
      <input
        type="checkbox"
        className={`mtf-checkbox__input ${inputClassName}`.trim()}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={ariaLabel}
      />
      <span className="mtf-checkbox__box" aria-hidden>
        {indeterminate ? <FaMinus /> : checked ? <FaCheck /> : null}
      </span>
    </Wrapper>
  );
}

const ManageTransportFee = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [sessionList, setSessionList] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [serviceMonths, setServiceMonths] = useState([]);
  const [selectedServiceMonth, setSelectedServiceMonth] = useState("");
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [filterRouteId, setFilterRouteId] = useState("");
  const [filterVehicleId, setFilterVehicleId] = useState("");
  const [filterPickupPointId, setFilterPickupPointId] = useState("");
  const [filterClassKey, setFilterClassKey] = useState("");
  const [addFeeAmount, setAddFeeAmount] = useState("");
  const [addFeeSelectedIds, setAddFeeSelectedIds] = useState([]);
  const [addFeeSubmitting, setAddFeeSubmitting] = useState(false);

  const selectedSession = useMemo(
    () => sessionList.find((s) => String(s.session_id) === String(sessionId)),
    [sessionList, sessionId]
  );

  const loadSessions = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE()}getSessionList.php`, {
        params: { school_code: schoolCode },
      });

      if (res.data?.api_status !== "OK" || !Array.isArray(res.data?.data)) {
        setSessionList([]);
        myToast.notify_warning(
          res.data?.api_remark || "Could not load academic sessions."
        );
        return;
      }

      const sessions = res.data.data;
      setSessionList(sessions);

      const current = SessionManager.getCurrentSession();
      const currentId =
        Array.isArray(current) && current[0]?.session_id
          ? String(current[0].session_id)
          : "";
      const defaultSession =
        sessions.find((s) => String(s.session_id) === currentId) || sessions[0];

      if (defaultSession?.session_id) {
        setSessionId(String(defaultSession.session_id));
      }
    } catch (error) {
      console.error("[ManageTransportFee] session load error", error);
      setSessionList([]);
      myToast.notify_warning("Network error loading sessions.");
    }
  }, []);

  const loadTransportStudents = useCallback(
    async (
      activeSessionId,
      serviceMonth,
      autoSelectMonth = false,
      includeSetup = true
    ) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !activeSessionId) return;

      setStudentsLoading(true);
      try {
        const params = {
          school_code: schoolCode,
          session_id: activeSessionId,
          include_setup: includeSetup ? "1" : "0",
        };
        if (serviceMonth) {
          params.service_month = serviceMonth;
        }

        const res = await axios.get(
          `${API_BASE()}getTransportStudentsBySession.php`,
          { params }
        );

        if (res.data?.api_status === "OK" && res.data?.data) {
          const payload = res.data.data;
          setStudents(Array.isArray(payload.students) ? payload.students : []);

          if (includeSetup) {
            setRoutes(Array.isArray(payload.routes) ? payload.routes : []);
            setVehicles(Array.isArray(payload.vehicles) ? payload.vehicles : []);
            setPickupPoints(
              Array.isArray(payload.pickup_points) ? payload.pickup_points : []
            );

            const months = Array.isArray(payload.service_months)
              ? payload.service_months
              : [];
            setServiceMonths(months);

            if (autoSelectMonth && months.length > 0 && !serviceMonth) {
              const firstMonth = months[0];
              setSelectedServiceMonth(firstMonth);
              await loadTransportStudents(
                activeSessionId,
                firstMonth,
                false,
                false
              );
              return;
            }

            if (serviceMonth && months.includes(serviceMonth)) {
              setSelectedServiceMonth(serviceMonth);
            } else if (!serviceMonth) {
              setSelectedServiceMonth("");
            }
          } else if (serviceMonth) {
            setSelectedServiceMonth(serviceMonth);
          }
        } else {
          setStudents([]);
          if (includeSetup) {
            setServiceMonths([]);
            setRoutes([]);
            setVehicles([]);
            setPickupPoints([]);
            setSelectedServiceMonth("");
          }
        }
      } catch (error) {
        console.error("[ManageTransportFee] students load error", error);
        setStudents([]);
        myToast.notify_warning("Network error loading transport students.");
      } finally {
        setStudentsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!sessionId) return;
    loadTransportStudents(sessionId, "", true);
  }, [sessionId, loadTransportStudents]);

  const handleSessionChange = (value) => {
    setSessionId(value);
    setFilterClassKey("");
    setFilterRouteId("");
    setFilterVehicleId("");
    setFilterPickupPointId("");
    setSelectedServiceMonth("");
    setAddFeeAmount("");
    setAddFeeSelectedIds([]);
  };

  const handleServiceMonthChange = (value) => {
    if (value === selectedServiceMonth) return;
    setSelectedServiceMonth(value);
    setAddFeeSelectedIds([]);
    if (sessionId && value) {
      loadTransportStudents(sessionId, value, false, false);
    }
  };

  const handleRefresh = () => {
    if (!sessionId) return;
    loadTransportStudents(
      sessionId,
      selectedServiceMonth,
      false,
      !selectedServiceMonth
    );
  };

  const classOptions = useMemo(() => {
    const map = new Map();
    students.forEach((row) => {
      if (!row.class_id) return;
      const sectionId = row.section_id ?? "";
      const key = `${row.class_id}|${sectionId}`;
      if (map.has(key)) return;

      const label = `${row.class_name || "Class"}${row.section_name ? ` · ${row.section_name}` : ""}`;
      map.set(key, { key, label });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" })
    );
  }, [students]);

  const filteredStudents = useMemo(() => {
    return students.filter((row) => {
      if (filterClassKey) {
        const [classId, sectionId] = filterClassKey.split("|");
        if (String(row.class_id) !== String(classId)) {
          return false;
        }
        if (sectionId && String(row.section_id ?? "") !== String(sectionId)) {
          return false;
        }
      }
      if (filterRouteId && String(row.route_id) !== String(filterRouteId)) {
        return false;
      }
      if (
        filterVehicleId &&
        String(row.vehicle_id) !== String(filterVehicleId)
      ) {
        return false;
      }
      if (
        filterPickupPointId &&
        String(row.pickup_point_id) !== String(filterPickupPointId)
      ) {
        return false;
      }
      return true;
    });
  }, [students, filterClassKey, filterRouteId, filterVehicleId, filterPickupPointId]);

  const monthlyFeeTotal = useMemo(() => {
    return filteredStudents.reduce((sum, row) => {
      const fee = Number(row.net_service_fee);
      return Number.isFinite(fee) ? sum + fee : sum;
    }, 0);
  }, [filteredStudents]);

  const paymentStats = useMemo(() => {
    const stats = { paid: 0, due: 0, pending: 0, noFee: 0 };
    if (!selectedServiceMonth) return stats;

    filteredStudents.forEach((row) => {
      const value = String(row.payment_status || "").toLowerCase();
      if (value === "paid" || value === "due settled") stats.paid += 1;
      else if (value === "due") stats.due += 1;
      else if (value === "no fee") stats.noFee += 1;
      else stats.pending += 1;
    });
    return stats;
  }, [filteredStudents, selectedServiceMonth]);

  const collectionStats = useMemo(() => {
    return filteredStudents.reduce(
      (acc, row) => {
        const paid = Number(row.amount_paid);
        const settled = Number(row.amount_settled);
        const outstanding = Number(row.outstanding);

        if (Number.isFinite(paid)) acc.collected += paid;
        if (Number.isFinite(settled)) acc.collected += settled;
        if (Number.isFinite(outstanding)) acc.outstanding += outstanding;
        return acc;
      },
      { collected: 0, outstanding: 0 }
    );
  }, [filteredStudents]);

  const hasActiveFilters =
    Boolean(filterClassKey) ||
    Boolean(filterRouteId) ||
    Boolean(filterVehicleId) ||
    Boolean(filterPickupPointId);

  const activeFilterCount = useMemo(() => {
    return [filterClassKey, filterRouteId, filterVehicleId, filterPickupPointId].filter(
      Boolean
    ).length;
  }, [filterClassKey, filterRouteId, filterVehicleId, filterPickupPointId]);

  const eligibleStudentsForAddFee = useMemo(() => {
    if (!selectedServiceMonth) return [];
    return filteredStudents.filter(isStudentEligibleForAddFee);
  }, [filteredStudents, selectedServiceMonth]);

  const addFeeSelectState = useMemo(() => {
    const eligibleIds = eligibleStudentsForAddFee.map((row) => String(row.student_id));
    const selectedEligibleCount = eligibleIds.filter((id) =>
      addFeeSelectedIds.includes(id)
    ).length;

    return {
      eligibleIds,
      allSelected:
        eligibleIds.length > 0 && selectedEligibleCount === eligibleIds.length,
      someSelected:
        selectedEligibleCount > 0 && selectedEligibleCount < eligibleIds.length,
    };
  }, [eligibleStudentsForAddFee, addFeeSelectedIds]);

  const toggleAddFeeStudent = (studentId) => {
    const id = String(studentId);
    setAddFeeSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleSelectAllAddFee = () => {
    const eligibleIds = eligibleStudentsForAddFee.map((row) => String(row.student_id));
    const allSelected =
      eligibleIds.length > 0 && eligibleIds.every((id) => addFeeSelectedIds.includes(id));

    if (allSelected) {
      setAddFeeSelectedIds((prev) => prev.filter((id) => !eligibleIds.includes(id)));
      return;
    }

    setAddFeeSelectedIds((prev) => Array.from(new Set([...prev, ...eligibleIds])));
  };

  const handleSubmitAddFee = async () => {
    const feeValue = Number(addFeeAmount);
    if (!selectedServiceMonth) {
      myToast.notify_warning("Select a service month first.");
      return;
    }
    if (!Number.isFinite(feeValue) || feeValue <= 0) {
      myToast.notify_warning("Enter a valid fee amount.");
      return;
    }
    if (addFeeSelectedIds.length === 0) {
      myToast.notify_warning("Select at least one student.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    const staffData = SessionManager.getStaffDetails();
    const staffId = staffData?.staff_id;

    if (!schoolCode || !staffId || !sessionId) {
      myToast.notify_warning("Session information is missing. Please login again.");
      return;
    }

    setAddFeeSubmitting(true);
    try {
      const res = await axios.get(`${API_BASE()}saveTransportServiceMonthFeeBulk.php`, {
        params: {
          staff_id: staffId,
          school_code: schoolCode,
          session_id: sessionId,
          service_month: selectedServiceMonth,
          net_service_fee: feeValue,
          student_ids: JSON.stringify(addFeeSelectedIds),
        },
      });

      if (res.data?.api_status === "OK") {
        myToast.notify_success(res.data?.api_remark || "Transport fee saved successfully.");
        setAddFeeAmount("");
        setAddFeeSelectedIds([]);
        await loadTransportStudents(sessionId, selectedServiceMonth, false, false);
      } else {
        myToast.notify_warning(
          res.data?.api_remark || "Could not save transport fee for selected students."
        );
      }
    } catch (error) {
      console.error("[ManageTransportFee] add fee error", error);
      myToast.notify_warning("Network error while saving transport fee.");
    } finally {
      setAddFeeSubmitting(false);
    }
  };

  const isBusy = studentsLoading || addFeeSubmitting;

  if (!loggedIn) return <Redirect to="/Login" />;
  if (!sessionId && sessionList.length === 0 && !isBusy) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page tp-page mtf-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div className="content-header" style={{ background: COLORS.primary_color_shade5 }}>
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <span style={{ color: COLORS.white }}>Manage Transport Fee</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp tp-shell">
        {isBusy && <MyLoader />}

        <header className="tp-hero mtf-hero" style={{ "--tp-hero-bg": HERO_GRADIENT }}>
          <div className="mtf-hero__title-row">
            <div className="tp-hero__brand">
              <div className="tp-hero__icon" aria-hidden>
                <FaRupeeSign />
              </div>
              <div>
                <h1 className="tp-hero__title">Manage Transport Fee</h1>
                <p className="tp-hero__sub">
                  {formatCount(students.length)} transport students
                  {selectedServiceMonth
                    ? ` · ${formatServiceMonth(selectedServiceMonth)}`
                    : ""}
                </p>
              </div>
            </div>
            <div className="tp-hero__tools">
              <select
                value={sessionId}
                onChange={(e) => handleSessionChange(e.target.value)}
                className="pp-pill-select pp-pill-select--session mtf-hero__session-select"
                aria-label="Academic session"
              >
                {sessionList.length === 0 ? (
                  <option value="">Loading sessions…</option>
                ) : (
                  sessionList.map((session) => (
                    <option key={session.session_id} value={session.session_id}>
                      {session.session_name}
                      {session.status ? ` · ${formatSessionStatus(session.status)}` : ""}
                    </option>
                  ))
                )}
              </select>
              {selectedSession?.status ? (
                <span
                  className={`mtf-session-status${
                    String(selectedSession.status).toLowerCase() === "current"
                      ? " mtf-session-status--current"
                      : ""
                  }`}
                >
                  {formatSessionStatus(selectedSession.status)}
                </span>
              ) : null}
              <button
                type="button"
                className={`pp-icon-btn ${isBusy ? "spinning" : ""}`}
                onClick={handleRefresh}
                disabled={isBusy || !sessionId}
                aria-label="Refresh transport fee data"
              >
                <FaSyncAlt aria-hidden />
              </button>
            </div>
          </div>

          <div className="mtf-hero__session-block">
            <div className="mtf-month-panel">
              <div className="mtf-hero__session-meta">
                <FaCalendarAlt aria-hidden />
                Service months
              </div>
              <div className="mtf-month-chips" role="group" aria-label="Service months">
                {serviceMonths.length === 0 ? (
                  <span className="mtf-month-empty">No service months available</span>
                ) : (
                  serviceMonths.map((month) => (
                    <button
                      key={month}
                      type="button"
                      className={`mtf-month-chip${
                        selectedServiceMonth === month ? " mtf-month-chip--active" : ""
                      }`}
                      onClick={() => handleServiceMonthChange(month)}
                      aria-pressed={selectedServiceMonth === month}
                    >
                      {formatServiceMonth(month)}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </header>

        <section className="tp-section mtf-summary" aria-label="Summary">
          <div className="mtf-summary__card">
            <span className="mtf-summary__icon mtf-summary__icon--students" aria-hidden>
              <FaUsers />
            </span>
            <div>
              <div className="mtf-summary__value">{formatCount(students.length)}</div>
              <div className="mtf-summary__label">Total students</div>
            </div>
          </div>
          <div className="mtf-summary__card">
            <span className="mtf-summary__icon mtf-summary__icon--filter" aria-hidden>
              <FaFilter />
            </span>
            <div>
              <div className="mtf-summary__value">{formatCount(filteredStudents.length)}</div>
              <div className="mtf-summary__label">Showing now</div>
            </div>
          </div>
          <div className="mtf-summary__card">
            <span className="mtf-summary__icon mtf-summary__icon--fee" aria-hidden>
              <FaRupeeSign />
            </span>
            <div>
              <div className="mtf-summary__value">
                {selectedServiceMonth ? formatINR(monthlyFeeTotal) : "—"}
              </div>
              <div className="mtf-summary__label">
                {selectedServiceMonth
                  ? `${formatServiceMonth(selectedServiceMonth)} total`
                  : "Select a month"}
              </div>
            </div>
          </div>
          {selectedServiceMonth ? (
            <>
              <div className="mtf-summary__card">
                <span className="mtf-summary__icon mtf-summary__icon--paid" aria-hidden>
                  <FaCheckCircle />
                </span>
                <div>
                  <div className="mtf-summary__value">
                    {formatINR(collectionStats.collected)}
                  </div>
                  <div className="mtf-summary__label">Collected</div>
                </div>
              </div>
              <div className="mtf-summary__card">
                <span className="mtf-summary__icon mtf-summary__icon--due" aria-hidden>
                  <FaClock />
                </span>
                <div>
                  <div className="mtf-summary__value">
                    {formatINR(collectionStats.outstanding)}
                  </div>
                  <div className="mtf-summary__label">Outstanding</div>
                </div>
              </div>
              <div className="mtf-summary__card">
                <span className="mtf-summary__icon mtf-summary__icon--filter" aria-hidden>
                  <FaCheckCircle />
                </span>
                <div>
                  <div className="mtf-summary__value">{formatCount(paymentStats.paid)}</div>
                  <div className="mtf-summary__label">Paid students</div>
                </div>
              </div>
            </>
          ) : null}
        </section>

        <section className="tp-section" aria-label="Student filters">
          <div
            className={`mtf-panel mtf-panel--filters${
              hasActiveFilters ? " mtf-panel--filters-active" : ""
            }`}
          >
            <div className="mtf-filters-bar">
              <div className="mtf-filters-bar__title">
                <span className="mtf-filters-bar__icon" aria-hidden>
                  <FaFilter />
                </span>
                <span>Filter students</span>
                {hasActiveFilters ? (
                  <span className="mtf-filters-bar__badge">{activeFilterCount}</span>
                ) : null}
              </div>
              {hasActiveFilters ? (
                <span className="mtf-filters-bar__meta">
                  Showing {formatCount(filteredStudents.length)} of{" "}
                  {formatCount(students.length)}
                </span>
              ) : null}
              <div className="mtf-filters-bar__controls">
                <select
                  id="mtf-filter-class"
                  className={`mtf-filters-bar__control${
                    filterClassKey ? " mtf-filters-bar__control--active" : ""
                  }`}
                  value={filterClassKey}
                  onChange={(e) => setFilterClassKey(e.target.value)}
                  aria-label="Filter by class"
                >
                  <option value="">All classes</option>
                  {classOptions.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select
                  id="mtf-filter-route"
                  className={`mtf-filters-bar__control${
                    filterRouteId ? " mtf-filters-bar__control--active" : ""
                  }`}
                  value={filterRouteId}
                  onChange={(e) => setFilterRouteId(e.target.value)}
                  aria-label="Filter by route"
                >
                  <option value="">All routes</option>
                  {routes.map((route) => (
                    <option key={route.route_id} value={route.route_id}>
                      {route.route_name}
                    </option>
                  ))}
                </select>
                <select
                  id="mtf-filter-vehicle"
                  className={`mtf-filters-bar__control${
                    filterVehicleId ? " mtf-filters-bar__control--active" : ""
                  }`}
                  value={filterVehicleId}
                  onChange={(e) => setFilterVehicleId(e.target.value)}
                  aria-label="Filter by vehicle"
                >
                  <option value="">All vehicles</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                      {vehicle.vehicle_name || vehicle.vehicle_number}
                      {vehicle.vehicle_number ? ` (${vehicle.vehicle_number})` : ""}
                    </option>
                  ))}
                </select>
                <select
                  id="mtf-filter-pickup"
                  className={`mtf-filters-bar__control${
                    filterPickupPointId ? " mtf-filters-bar__control--active" : ""
                  }`}
                  value={filterPickupPointId}
                  onChange={(e) => setFilterPickupPointId(e.target.value)}
                  aria-label="Filter by pickup point"
                >
                  <option value="">All pickup points</option>
                  {pickupPoints.map((point) => (
                    <option key={point.pickup_point_id} value={point.pickup_point_id}>
                      {point.pickup_point_name}
                    </option>
                  ))}
                </select>
              </div>
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="mtf-filters-bar__clear"
                  onClick={() => {
                    setFilterClassKey("");
                    setFilterRouteId("");
                    setFilterVehicleId("");
                    setFilterPickupPointId("");
                  }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <section className="tp-section tp-section--last" aria-label="Student list">
          <div className="mtf-panel mtf-panel--table">
            <div className="mtf-table-toolbar">
              <div className="mtf-table-toolbar__left">
                <div className="mtf-table-toolbar__fee-group">
                  <label className="mtf-table-toolbar__fee" htmlFor="mtf-add-fee-amount">
                    <FaRupeeSign aria-hidden />
                    <input
                      id="mtf-add-fee-amount"
                      type="number"
                      min="1"
                      step="1"
                      className="mtf-table-toolbar__fee-input"
                      value={addFeeAmount}
                      onChange={(e) => setAddFeeAmount(e.target.value)}
                      placeholder="Amount to add"
                      disabled={isBusy || !selectedServiceMonth}
                      aria-label="Amount to add to fee"
                    />
                  </label>
                  <button
                    type="button"
                    className="mtf-table-toolbar__submit"
                    onClick={handleSubmitAddFee}
                    disabled={
                      isBusy ||
                      !sessionId ||
                      !selectedServiceMonth ||
                      addFeeSelectedIds.length === 0
                    }
                  >
                    <FaPlus aria-hidden />
                    {addFeeSubmitting ? "Saving..." : "Add Fee"}
                  </button>
                </div>
                {selectedServiceMonth ? (
                  <label
                    className={`mtf-table-toolbar__select-all${
                      addFeeSelectState.allSelected ? " mtf-table-toolbar__select-all--active" : ""
                    }${
                      addFeeSelectState.someSelected
                        ? " mtf-table-toolbar__select-all--partial"
                        : ""
                    }`}
                  >
                    <AddFeeCheckbox
                      nested
                      checked={addFeeSelectState.allSelected}
                      indeterminate={addFeeSelectState.someSelected}
                      disabled={isBusy || eligibleStudentsForAddFee.length === 0}
                      onChange={toggleSelectAllAddFee}
                      inputClassName="mtf-table-toolbar__select-all-input"
                      aria-label="Select all eligible students"
                    />
                    <span className="mtf-table-toolbar__select-all-text">Select all eligible</span>
                    <span className="mtf-table-toolbar__select-all-count">
                      {formatCount(eligibleStudentsForAddFee.length)}
                    </span>
                  </label>
                ) : null}
              </div>
              <div className="mtf-table-toolbar__right">
                {selectedServiceMonth ? (
                  <span className="mtf-table-toolbar__meta">
                    {formatServiceMonth(selectedServiceMonth)} ·{" "}
                    {formatCount(addFeeSelectedIds.length)} selected ·{" "}
                    {formatCount(eligibleStudentsForAddFee.length)} eligible
                  </span>
                ) : (
                  <span className="mtf-table-toolbar__meta mtf-table-toolbar__meta--muted">
                    Select a service month to add fee
                  </span>
                )}
              </div>
            </div>
          <div className="mtf-table-wrap">
            <table className="mtf-table">
              <colgroup>
                {selectedServiceMonth ? <col className="mtf-col mtf-col--select" /> : null}
                <col className="mtf-col mtf-col--index" />
                <col className="mtf-col mtf-col--student" />
                <col className="mtf-col mtf-col--class" />
                <col className="mtf-col mtf-col--route" />
                <col className="mtf-col mtf-col--vehicle" />
                <col className="mtf-col mtf-col--pickup" />
                {selectedServiceMonth ? (
                  <>
                    <col className="mtf-col mtf-col--fee" />
                    <col className="mtf-col mtf-col--amount" />
                    <col className="mtf-col mtf-col--amount" />
                    <col className="mtf-col mtf-col--amount" />
                  </>
                ) : null}
              </colgroup>
              <thead className="mtf-table__head">
                <tr>
                  {selectedServiceMonth ? (
                    <th className="mtf-th mtf-th--select">
                      <span className="mtf-th__inner mtf-th__inner--center">
                        <AddFeeCheckbox
                          checked={addFeeSelectState.allSelected}
                          indeterminate={addFeeSelectState.someSelected}
                          disabled={isBusy || eligibleStudentsForAddFee.length === 0}
                          onChange={toggleSelectAllAddFee}
                          aria-label="Select all eligible students in table"
                        />
                      </span>
                    </th>
                  ) : null}
                  <TableHeaderCell label="#" className="mtf-th--index" />
                  <TableHeaderCell label="Student" icon={FaUsers} className="mtf-th--student" />
                  <TableHeaderCell label="Class" icon={FaUserGraduate} className="mtf-th--class" />
                  <TableHeaderCell label="Route" icon={FaRoute} className="mtf-th--route" />
                  <TableHeaderCell label="Vehicle" icon={FaBus} className="mtf-th--vehicle" />
                  <TableHeaderCell label="Pickup" icon={FaMapMarkerAlt} className="mtf-th--pickup" />
                  {selectedServiceMonth ? (
                    <TableHeaderCell
                      label="Fee"
                      icon={FaRupeeSign}
                      className="mtf-th--fee mtf-th--group-start"
                    />
                  ) : null}
                  {selectedServiceMonth ? (
                    <TableHeaderCell
                      label="Paid"
                      icon={FaCheckCircle}
                      className="mtf-th--paid"
                    />
                  ) : null}
                  {selectedServiceMonth ? (
                    <TableHeaderCell label="Due" icon={FaClock} className="mtf-th--due" />
                  ) : null}
                  {selectedServiceMonth ? (
                    <TableHeaderCell
                      label="Settled"
                      icon={FaReceipt}
                      className="mtf-th--settled"
                    />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={selectedServiceMonth ? 11 : 6}>
                      <div className="mtf-empty">
                        <div className="mtf-empty__icon" aria-hidden>
                          <FaUsers />
                        </div>
                        <p className="mtf-empty__title">No students found</p>
                        <p className="mtf-empty__hint">
                          Try another service month or clear your filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((row, index) => {
                    const studentId = String(row.student_id);
                    const canSelect = isStudentEligibleForAddFee(row);
                    const isSelected = addFeeSelectedIds.includes(studentId);

                    return (
                    <tr
                      key={`${row.student_id}-${index}`}
                      className={`${getRowPaymentClass(row.payment_status)}${
                        isSelected ? " mtf-row--selected" : ""
                      }`}
                    >
                      {selectedServiceMonth ? (
                        <td className="mtf-select-cell">
                          <AddFeeCheckbox
                            checked={isSelected}
                            locked={!canSelect}
                            onChange={() => toggleAddFeeStudent(studentId)}
                            disabled={addFeeSubmitting || !canSelect}
                            aria-label={`Select ${row.student_name || studentId}`}
                          />
                        </td>
                      ) : null}
                      <td className="mtf-row-num">{index + 1}</td>
                      <td className="mtf-cell mtf-cell--student">
                        <div className="mtf-student-cell">
                          <span className="mtf-student-cell__text">
                            <div
                              className="mtf-student-cell__name"
                              title={row.student_name || undefined}
                            >
                              {row.student_name || "—"}
                            </div>
                            <div className="mtf-student-cell__id">{row.student_id}</div>
                          </span>
                        </div>
                      </td>
                      <td className="mtf-cell mtf-cell--class">
                        <span
                          className="mtf-class-pill"
                          title={
                            row.class_name
                              ? `${row.class_name}${row.section_name ? ` · ${row.section_name}` : ""}`
                              : undefined
                          }
                        >
                          {row.class_name || "—"}
                          {row.section_name ? ` · ${row.section_name}` : ""}
                        </span>
                      </td>
                      <td className="mtf-cell mtf-cell--route">
                        {row.route_name ? (
                          <span className="mtf-tag mtf-tag--route" title={row.route_name}>
                            {row.route_name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="mtf-cell mtf-cell--vehicle">
                        {row.vehicle_name || row.vehicle_number ? (
                          <span
                            className="mtf-tag"
                            title={row.vehicle_name || row.vehicle_number}
                          >
                            {row.vehicle_name || row.vehicle_number}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="mtf-cell mtf-cell--pickup">
                        {row.pickup_point_name ? (
                          <span className="mtf-tag" title={row.pickup_point_name}>
                            {row.pickup_point_name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      {selectedServiceMonth ? (
                        <td className="mtf-fee-value">
                          {formatMoneyValue(row.fee_value ?? row.net_service_fee)}
                        </td>
                      ) : null}
                      {selectedServiceMonth ? (
                        <td>
                          <span className={getPaidAmountClass(row.amount_paid)}>
                            {formatMoneyValue(row.amount_paid)}
                          </span>
                        </td>
                      ) : null}
                      {selectedServiceMonth ? (
                        <td>
                          <span className={getDueAmountClass(row.outstanding, row.payment_status)}>
                            {formatMoneyValue(row.outstanding)}
                          </span>
                        </td>
                      ) : null}
                      {selectedServiceMonth ? (
                        <td className="mtf-settled">
                          {formatMoneyValue(row.amount_settled)}
                        </td>
                      ) : null}
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ManageTransportFee;
